import ReactPlayer from "react-player";
import { Container, makeStyles } from "@material-ui/core";
import React, { useRef, useState } from "react";
import screenfull from "screenfull";
import Control from "./Control";

export const formatTime = time => {
  // formarting duration of video
  if (Number.isNaN(time)) {
    return "00:00";
  }

  const date = new Date(time * 1000);
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const seconds = date.getUTCSeconds().toString().padStart(2, "0");
  if (hours) {
    // if video have hours
    return `${hours}:${minutes.toString().padStart(2, "0")} `;
  }
  return `${minutes}:${seconds}`;
};

let count = 0;

const useStyles = makeStyles({
  videoContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    height: "100%"
  },
  playerWrapper: {
    position: "relative",
    height: "100%",
    aspectRatio: "16/9",
    margin: "0 auto"
  },
  player: {
    objectFit: "cover",
    padding: "0",
    margin: "0"
  },
  containerVideo: {
    height: "100%",
    maxWidth: "unset",
    padding: "0"
  },
  hiddenControls: {
    visibility: "hidden"
  }
});

const VideoPlayer = ({ videoUrl, startingPercentage, onProgress, disableSkipForward }) => {
  const classes = useStyles();
  const videoPlayerRef = useRef(null);
  const controlRef = useRef(null);

  const config = {
    youtube: {
      playerVars: {
        disablekb: 0,
        rel: 0,
        showinfo: 0,
        modestbranding: 1,
        autoplay: 1
      }
    },
    vimeo: {
      playerOptions: {
        autoplay: 1,
        keyboard: 0
      }
    }
  };

  const [videoState, setVideoState] = useState({
    muted: false,
    volume: 0.5,
    playbackRate: 1.0,
    played: 0,
    maxPlayed: 0,
    seeking: false,
    buffer: true,
    fullscreen: false
  });

  const [playing, setPlaying] = useState(false);
  const [showControls, setShowControls] = useState(false);

  // Destructuring the properties from the videoState
  const { muted, volume, playbackRate, played, seeking, maxPlayed } = videoState;

  const currentTime = videoPlayerRef.current ? videoPlayerRef.current.getCurrentTime() : "00:00";
  const duration = videoPlayerRef.current ? videoPlayerRef.current.getDuration() : "00:00";

  const formatCurrentTime = formatTime(currentTime);
  const formatDuration = formatTime(duration);

  const playPauseHandler = () => {
    setPlaying(!playing);
  };

  const startHandler = async () => {
    setShowControls(true);
    setPlaying(true);
  };

  const readyHandler = async player => {
    setVideoState({ ...videoState, seeking: true });
    if (startingPercentage === 0 || startingPercentage === 100) {
      return;
    }
    if (player.getInternalPlayer().origin === "https://player.vimeo.com") {
      const videoDuration = await player.getInternalPlayer().getDuration();
      const secondsStart = Math.floor((videoDuration * startingPercentage) / 100);
      player.getInternalPlayer().setCurrentTime(secondsStart);
    } else {
      const videoDuration = player.getInternalPlayer().getDuration();
      const secondsStart = Math.floor((videoDuration * startingPercentage) / 100);
      player.getInternalPlayer().seekTo(secondsStart);
    }
    setVideoState({ ...videoState, seeking: false });
  };

  const rewindHandler = () => {
    videoPlayerRef.current.seekTo(videoPlayerRef.current.getCurrentTime() - 5);
  };

  const handleFastFoward = () => {
    videoPlayerRef.current.seekTo(videoPlayerRef.current.getCurrentTime() + 10);
  };

  const progressHandler = state => {
    if (count > 0) {
      controlRef.current.style.visibility = "hidden";
    } else if (controlRef.current.style.visibility === "visible") {
      count += 1;
    }

    if (!seeking) {
      setVideoState({ ...videoState, ...state });
      if (played > maxPlayed) {
        setVideoState({ ...videoState, maxPlayed: state.played });
      }

      onProgress({ played: state.played });
    }
  };

  const seekEvent = (e, value) => {
    if (disableSkipForward) {
      const changeValue = Math.min(value / 100, maxPlayed);
      setVideoState({ ...videoState, played: changeValue });
      videoPlayerRef.current.seekTo(changeValue);
    } else {
      setVideoState({ ...videoState, played: value / 100 });
      videoPlayerRef.current.seekTo(value / 100);
    }
  };

  const seekHandler = (e, value) => {
    seekEvent(e, value);
  };

  const seekMouseUpHandler = (e, value) => {
    seekEvent(e, value);
  };

  const volumeChangeHandler = (e, value) => {
    const newVolume = parseFloat(value) / 100;

    setVideoState({
      ...videoState,
      volume: newVolume,
      muted: Number(newVolume) === 0 // volume === 0 then muted
    });
  };

  const volumeSeekUpHandler = (e, value) => {
    const newVolume = parseFloat(value) / 100;

    setVideoState({
      ...videoState,
      volume: newVolume,
      muted: newVolume === 0
    });
  };

  const muteHandler = () => {
    setVideoState({ ...videoState, muted: !videoState.muted });
  };

  const fullscreenHandler = () => {
    if (!videoState.fullscreen) {
      screenfull.request(videoPlayerRef.current.wrapper);
    }
    setVideoState({ ...videoState, fullscreen: !videoState.fullscreen });
  };

  const onSeekMouseDownHandler = () => {
    setVideoState({ ...videoState, seeking: true });
  };

  const mouseMoveHandler = () => {
    if (showControls) {
      controlRef.current.style.visibility = "visible";
      count = 0;
    }
  };

  const mouseLeaveHandler = () => {
    controlRef.current.style.visibility = "hidden";
  };

  const bufferStartHandler = () => {
    setVideoState({ ...videoState, buffer: true });
  };

  const bufferEndHandler = () => {
    setVideoState({ ...videoState, buffer: false });
  };

  return (
    <div className={classes.videoContainer}>
      <Container className={classes.containerVideo} maxWidth="md" justify="center">
        <div
          className={classes.playerWrapper}
          onMouseLeave={mouseLeaveHandler}
          onMouseMove={mouseMoveHandler}
        >
          <ReactPlayer
            ref={videoPlayerRef}
            className={classes.player}
            url={videoUrl}
            width="100%"
            height="100%"
            playing={playing}
            volume={volume}
            muted={muted}
            config={config}
            light
            onStart={startHandler}
            onReady={readyHandler}
            onProgress={progressHandler}
            onBuffer={bufferStartHandler}
            onBufferEnd={bufferEndHandler}
          />

          <div className={showControls ? "" : classes.hiddenControls}>
            <Control
              controlRef={controlRef}
              playing={playing}
              canRewind
              canFastForward={false}
              played={played}
              volume={volume}
              mute={muted}
              playRate={playbackRate}
              duration={formatDuration}
              currentTime={formatCurrentTime}
              onPlayPause={playPauseHandler}
              onRewind={rewindHandler}
              onForward={handleFastFoward}
              onSeek={seekHandler}
              onSeekMouseUp={seekMouseUpHandler}
              onVolumeChangeHandler={volumeChangeHandler}
              onVolumeSeekUp={volumeSeekUpHandler}
              onMute={muteHandler}
              onFullscreen={fullscreenHandler}
              onMouseSeekDown={onSeekMouseDownHandler}
            />
          </div>
        </div>
      </Container>
    </div>
  );
};

export default VideoPlayer;
